import React, { useRef, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { usePopupState, bindTrigger, bindMenu, anchorRef } from 'material-ui-popup-state/hooks';
import { Box, Divider } from '@mui/material';

import config, { DeleteMemberAction } from '@occc/config';
import {
  Typography,
  ArrowDropDownIcon,
  DeleteForeverOutlinedIcon,
  ListItemText,
} from '@occc-app/design-system';
import { Membership_State } from '@occc/graphql';
import { useTheme } from '@mui/material/styles';

import MemberDeleteModal from './MemberDeleteModal';

type MemberStateMenu = {
  selectedState: Membership_State;
  onMemberStateChange: (state: Membership_State) => Promise<void>;
  onMemberDelete: () => Promise<unknown>;
  onMemberScrubData: () => Promise<unknown>;
  isUserPermittedToUpdateMemberState: boolean;
  isDeletionInProgress: boolean;
  memberPhone?: string;
  customerUuid?: string | null;
  memberState?: Membership_State;
};

type DeleteMethod = 'scrub_data' | 'membership_delete' | 'delete_placeholder';

/**
 * Menu entry component for use in the MemberStateMenu.
 *
 * @note Uses React.forwardRef because the material UI Menu
 * component requires setting refs on its child components.
 */
export const labels: Record<Membership_State, string> = {
  [Membership_State.NewMember]: 'New member',
  [Membership_State.Active]: 'Active',
  [Membership_State.Suspended]: 'Suspended',
  [Membership_State.Paused]: 'Paused',
  [Membership_State.Deleted]: 'Deleted',
  [Membership_State.DeletionPlaceholder]: 'Deletion Placeholder',
};

const getMenuOptions = (state: Membership_State) => {
  if (state === Membership_State.Deleted) return [];
  if (state === Membership_State.DeletionPlaceholder) {
    return [Membership_State.NewMember];
  }
  return [
    Membership_State.NewMember,
    Membership_State.Active,
    Membership_State.Paused,
    Membership_State.Suspended,
  ];
};

type MembershipStatusVariant = 'info' | 'success' | 'warning' | 'error';

// Map membership states to standard MUI palette variants
const membershipStateToStatusVariant: Record<Membership_State, MembershipStatusVariant> = {
  [Membership_State.NewMember]: 'info',
  [Membership_State.Active]: 'success',
  [Membership_State.Suspended]: 'error',
  [Membership_State.Paused]: 'warning',
  [Membership_State.Deleted]: 'error',
  [Membership_State.DeletionPlaceholder]: 'error',
};

type StatusIndicatorProps = {
  state: Membership_State;
};

const StatusIndicator = ({ state }: StatusIndicatorProps) => {
  const theme = useTheme();
  const variant = membershipStateToStatusVariant[state];
  const color = theme.palette[variant].main;

  return <Box width={12} height={12} borderRadius='50%' marginRight={0.5} bgcolor={color} />;
};

/**
 * Dropdown menu component which allows for changing the "state" of
 * a membership (for ex. an "Active" user can be updated to be
 * in the "Suspended" state).
 */
const MemberStateMenu: React.FC<MemberStateMenu> = ({
  onMemberStateChange,
  selectedState,
  isUserPermittedToUpdateMemberState,
  memberPhone,
  onMemberDelete,
  onMemberScrubData,
  isDeletionInProgress,
  customerUuid,
  memberState,
}) => {
  const popupState = usePopupState({ variant: 'popover', popupId: 'state-menu' });
  const [memberDeleteModalShow, setMemberDeleteModalShow] = useState<boolean>(false);
  const deleteMethod = useRef<DeleteMethod>();

  const deleteAction = config.features.deleteMemberAction;

  const isEditable = selectedState !== Membership_State.Deleted;
  const isDeleteMemberFeatureEnabled = config.features.deleteMemberEnabled;
  const areDeleteMemberMethodsAvailable =
    memberPhone &&
    selectedState !== Membership_State.DeletionPlaceholder &&
    selectedState !== Membership_State.Suspended;

  const isScrubDataButtonVisible =
    customerUuid &&
    isDeleteMemberFeatureEnabled &&
    areDeleteMemberMethodsAvailable &&
    (deleteAction === DeleteMemberAction.BOTH || deleteAction === DeleteMemberAction.SCRUB_DATA);
  const isDeleteButtonVisible =
    isDeleteMemberFeatureEnabled &&
    areDeleteMemberMethodsAvailable &&
    (deleteAction === DeleteMemberAction.BOTH ||
      deleteAction === DeleteMemberAction.MEMBERSHIP_DELETE);
  const isDeletePlaceholderButtonVisible = memberState === Membership_State.DeletionPlaceholder;

  const isDeleteMemberSectionVisible =
    memberPhone &&
    (isScrubDataButtonVisible || isDeleteButtonVisible || isDeletePlaceholderButtonVisible);

  const onSelect = (value: Membership_State) => {
    onMemberStateChange(value);
    popupState.close();
  };

  const onMenuItemClick = (deleteMethodName: DeleteMethod) => {
    deleteMethod.current = deleteMethodName;
    setMemberDeleteModalShow(true);
  };

  const onConfirm = async () => {
    if (deleteMethod.current === 'membership_delete') {
      await onMemberDelete();
    }

    if (deleteMethod.current === 'scrub_data') {
      await onMemberScrubData();
    }

    if (deleteMethod.current === 'delete_placeholder') {
      await onMemberStateChange(Membership_State.Deleted);
    }

    setMemberDeleteModalShow(false);
    popupState.close();
  };

  if (!isUserPermittedToUpdateMemberState) {
    return (
      <Box
        role='button'
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'common.white',
          color: 'text.primary',
          borderRadius: 24,
          px: 0.75,
        }}
      >
        <StatusIndicator state={selectedState} />
        <Typography component='span' variant='body2'>
          <span>{labels[selectedState]}</span>
        </Typography>
      </Box>
    );
  }

  return (
    // TODO Consider creating a common component to avoid duplication MemberStateMenu && MemberTypeDropdown
    <Box display='flex' alignItems='center' data-testid='member-state-menu-wrapper'>
      <Box
        role='button'
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'common.white',
          color: 'text.primary',
          borderRadius: 24,
          px: 0.75,
          cursor: isEditable ? 'pointer' : 'not-allowed',
          '&:hover': { opacity: isEditable ? 0.8 : 1 },
        }}
        data-testid='open-menu-button'
        ref={anchorRef(popupState)}
        {...bindTrigger(popupState)}
      >
        <StatusIndicator state={selectedState} />
        <Typography component='span' variant='body2'>
          <span>{labels[selectedState]}</span>
        </Typography>
        <ArrowDropDownIcon />
      </Box>
      {isEditable && (
        <Menu {...bindMenu(popupState)}>
          {getMenuOptions(selectedState).map((value) => {
            return (
              <MenuItem
                key={value}
                onClick={() => onSelect(value)}
                selected={selectedState === value}
              >
                <ListItemText primary={labels[value]} />
              </MenuItem>
            );
          })}
          {isDeleteMemberSectionVisible && (
            <>
              <Divider sx={{ my: 1, mx: 2 }} />
              {isDeleteButtonVisible && (
                <MenuItem
                  onClick={() => onMenuItemClick('membership_delete')}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemText primary='Delete Member' sx={{ mr: 1 }} />
                  <DeleteForeverOutlinedIcon sx={{ ml: 'auto' }} />
                </MenuItem>
              )}
              {isScrubDataButtonVisible && (
                <MenuItem
                  onClick={() => onMenuItemClick('scrub_data')}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemText
                    primary={
                      deleteAction === DeleteMemberAction.BOTH ? 'Scrub Data' : 'Delete Member'
                    }
                    sx={{ mr: 1 }}
                  />
                  <DeleteForeverOutlinedIcon sx={{ ml: 'auto' }} />
                </MenuItem>
              )}
              {isDeletePlaceholderButtonVisible && (
                <MenuItem
                  onClick={() => onMenuItemClick('delete_placeholder')}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemText primary='Delete Placeholder' sx={{ mr: 1 }} />
                  <DeleteForeverOutlinedIcon sx={{ ml: 'auto' }} />
                </MenuItem>
              )}

              <MemberDeleteModal
                show={memberDeleteModalShow}
                onHide={() => setMemberDeleteModalShow(false)}
                onConfirm={onConfirm}
                memberPhone={memberPhone}
                isDeletionInProgress={isDeletionInProgress}
              />
            </>
          )}
        </Menu>
      )}
    </Box>
  );
};

export default MemberStateMenu;
